import React, { useContext, useState, useEffect, useReducer, useMemo } from 'react';
import { find, map, keyBy, filter } from 'lodash';
import { useHistory } from 'react-router-dom';
import ConfirmBox from '../../../../components/common/ConfirmBox';
import { StateContext } from '../../../../App';
import { routes } from '../../../data/constants';
import { v4 } from 'uuid';
import { advertisementDeleteHandler, advertisementsSetHandler } from '../actions';
import { reducer, initialState } from '../reducer';
import cogoToast from 'cogo-toast';
import LoadingSkeleton from './LoadingSkeleton';
import Search from '../../../../components/common/Search';

/* global __COMPONENTS_BUNDLE_SOURCE__ */
const componentsBundle = typeof __COMPONENTS_BUNDLE_SOURCE__ === 'string' ? __COMPONENTS_BUNDLE_SOURCE__ : '';
// Match leniently: ignore case and treat spaces/underscores as hyphens, so a group named
// "Vehicle Detail Top" still matches the "vehicle-detail-top" slug hardcoded in dealer-website-components.
const normalizeGroupName = (name) =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// Collect the group ids dealer-website-components actually passes to <AdvertiseComponent groupId=... />.
// Scoped to the `groupId:` prop (not any quoted string) so CSS classes etc. can't cause false positives.
// Computed once at module load — the bundle source is a build-time constant.
const usedGroupIdsInComponents = (() => {
  const ids = new Set();
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // `{groupId:"slug"` / `,groupId:'slug'` (inline literal) or `groupId:ident` (a local variable).
  const propRe = /[{,]\s*groupId\s*:\s*(?:"([^"]+)"|'([^']+)'|([A-Za-z_$][\w$.]*))/g;
  const identifiers = new Set();
  let m;
  while ((m = propRe.exec(componentsBundle)) !== null) {
    const literal = m[1] || m[2];
    if (literal) ids.add(normalizeGroupName(literal));
    // Skip member expressions like `t.groupId` (config-driven pass-through, not statically resolvable).
    else if (m[3] && !m[3].includes('.')) identifiers.add(m[3]);
  }
  // Resolve the campaign ternary pattern `ident = cond ? "slug-campaign" : "slug"` for each local variable.
  // Only hyphenated branches are kept, to avoid picking up reused vars assigned CSS values (e.g. `x=u?"block":"hidden"`).
  identifiers.forEach((id) => {
    const ternaryRe = new RegExp(escapeRegExp(id) + '=[^;{}]{0,80}?\\?"([^"]+)":"([^"]+)"', 'g');
    let t;
    while ((t = ternaryRe.exec(componentsBundle)) !== null) {
      [t[1], t[2]].forEach((branch) => {
        if (branch.includes('-')) ids.add(normalizeGroupName(branch));
      });
    }
  });
  return ids;
})();

// Recursively collect every `groupId` string referenced anywhere in the CMS page config
// (context.pages[].blocks[].content[].fields[].value.groupId, at any nesting depth).
// These come from the backend; collected raw so we can match them against both group id and name.
const collectGroupIdRefs = (node, acc) => {
  if (!node || typeof node !== 'object') return acc;
  if (Array.isArray(node)) {
    node.forEach((item) => collectGroupIdRefs(item, acc));
    return acc;
  }
  Object.keys(node).forEach((key) => {
    const val = node[key];
    if (key === 'groupId' && typeof val === 'string' && val.trim() !== '') {
      acc.add(val.trim());
    } else if (val && typeof val === 'object') {
      collectGroupIdRefs(val, acc);
    }
  });
  return acc;
};

export const AdsQuery = `query Advertisements{
  advertisements {
    id
    description
    content{
      culture
    }
    isDraft
    disabled
    groups
    filters
  }
  advertisementGroups {
    id
    name
  }
}`;

export const AdDeleteMutation = `mutation AdvertisementDelete($data: AdvertisementDeleteInputType!) {
  advertisementDelete(data: $data) {
    ok
  }
}`;

const Overview = ({ context, currentLanguage, setCurrentLanguage, onChangeEnvironment }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const contextState = useContext(StateContext);
  const history = useHistory();

  const [confirmDelete, setConfirmDelete] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const currentWebsite = find(contextState?.websites, (w) => w.id === contextState.currentWebsite);
  const isCurrentSiteProduction = currentWebsite ? currentWebsite.isProduction : false;

  const advertisements = state.advertisements;
  const advertisementGroups = state.advertisementGroups;
  const groupsById = keyBy(advertisementGroups, 'id');
  const languages = contextState?.configuration?.website?.languages;

  // Set of group names that are actually used: either dealer-website-components references them (a hardcoded slot),
  // or the CMS page config references them (context.pages → ... → fields → value.groupId).
  const usedGroupNames = useMemo(() => {
    // CMS refs may be the group id (UUID) or the slug, so normalize and match against both id and name.
    const cmsRefs = new Set();
    collectGroupIdRefs(context?.pages, new Set()).forEach((ref) => cmsRefs.add(normalizeGroupName(ref)));

    const used = new Set();
    (advertisementGroups || []).forEach((group) => {
      if (!group?.name) return;
      const normalizedName = normalizeGroupName(group.name);
      const inComponents = usedGroupIdsInComponents.has(normalizedName);
      const inCms = cmsRefs.has(normalizedName) || cmsRefs.has(normalizeGroupName(group.id));
      if (inComponents || inCms) {
        used.add(group.name);
      }
    });
    return used;
  }, [advertisementGroups, context?.pages]);

  // An advertisement is "active" if at least one of its groups is actually used (wired in components or CMS).
  const isAdvertisementActive = (advertisement) =>
    (advertisement?.groups || []).some((groupId) => {
      const group = groupsById[groupId];
      return group?.name ? usedGroupNames.has(group.name) : false;
    });

  // Only edit/delete drafts in draft environment (production posts are not editable in draft).
  const canEditAdvertisement = (advertisement) => !(advertisement.isDraft && !isCurrentSiteProduction);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const searchTerm = search.trim().toLowerCase();
  const filteredAdvertisements = filter(advertisements, (ad) => {
    if (statusFilter !== 'all') {
      const active = isAdvertisementActive(ad);
      if (statusFilter === 'active' && !active) return false;
      if (statusFilter === 'inactive' && active) return false;
    }

    if (searchTerm === '') return true;

    const description = (ad.description || '').toLowerCase();
    const groupNames = map(ad.groups, (groupId) => groupsById[groupId]?.name || '')
      .join(' ')
      .toLowerCase();
    const filterText = Array.isArray(ad.filters)
      ? map(ad.filters, (adFilter) => `${adFilter.type} ${adFilter.value}`)
          .join(' ')
          .toLowerCase()
      : '';
    return description.includes(searchTerm) || groupNames.includes(searchTerm) || filterText.includes(searchTerm);
  });

  // Select-all applies to the currently visible (filtered) advertisements that can be deleted.
  const selectableIds = filter(filteredAdvertisements, (ad) => canEditAdvertisement(ad)).map((ad) => ad.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectableIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) selectableIds.forEach((id) => next.delete(id));
      else selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const fetchAds = () => {
    fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: AdsQuery })
    })
      .then((resp) => resp.json())
      .then((result) => {
        advertisementsSetHandler(dispatch, result.data.advertisements, result.data.advertisementGroups);
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const id = confirmDelete;

    fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: AdDeleteMutation, variables: { data: { id: id } } })
    })
      .then((resp) => resp.json())
      .then((result) => {
        if (result?.data?.advertisementDelete?.ok) {
          advertisementDeleteHandler(dispatch, id);
          cogoToast.success('Advertisement deleted');
        } else {
          const error = result && result.errors && result.errors.length > 0 ? result.errors[0].message : 'Something went wrong';
          cogoToast.error(error);
        }
        setConfirmDelete('');
      })
      .catch((err) => {});
  };

  const handleBulkDelete = () => {
    const ids = [...selectedIds];

    Promise.all(
      ids.map((id) =>
        fetch('/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query: AdDeleteMutation, variables: { data: { id: id } } })
        })
          .then((resp) => resp.json())
          .then((result) => ({ id, ok: !!result?.data?.advertisementDelete?.ok }))
          .catch(() => ({ id, ok: false }))
      )
    ).then((results) => {
      const deleted = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);

      deleted.forEach((r) => advertisementDeleteHandler(dispatch, r.id));

      if (deleted.length > 0) {
        cogoToast.success(`${deleted.length} advertisement${deleted.length > 1 ? 's' : ''} deleted`);
      }
      if (failed.length > 0) {
        cogoToast.error(`${failed.length} advertisement${failed.length > 1 ? 's' : ''} could not be deleted`);
      }

      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
    });
  };

  const onEditAdvertisement = (id, lng) => {
    history.push(`${routes.ADVERTISEMENT_DETAIL}/${id}`, {
      language: lng
    });
  };

  const onCreateAdvertisement = () => {
    const id = v4();

    history.push(`${routes.ADVERTISEMENT_DETAIL}/${id}`, {
      language: 'nl'
    });
  };

  useEffect(() => {
    fetchAds();
  }, []);

  if (state.advertisements === undefined || state.advertisementsLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className='builder-flex builder-flex-col builder-p-5 builder-w-full'>
      {confirmDelete !== '' && <ConfirmBox title='Delete advertisement' text='Are you sure you want to delete this advertisement?' type='DANGER' onCancel={() => setConfirmDelete('')} onConfirm={handleDelete} />}
      {confirmBulkDelete && (
        <ConfirmBox
          title='Delete advertisements'
          text={`Are you sure you want to delete ${selectedIds.size} selected advertisement${selectedIds.size > 1 ? 's' : ''}?`}
          type='DANGER'
          onCancel={() => setConfirmBulkDelete(false)}
          onConfirm={handleBulkDelete}
        />
      )}

      <div className='builder-flex builder-justify-between builder-items-center builder-mb-5 builder-space-x-4'>
        <div className='builder-text-2xl builder-text-black builder-font-bold'>Advertisements</div>
        <div className='builder-flex builder-items-center builder-space-x-4'>
          {selectedIds.size > 0 && (
            <div
              onClick={() => setConfirmBulkDelete(true)}
              data-testid='advertisements-bulk-delete-button'
              className='builder-px-4 builder-py-2 builder-bg-red-600 builder-text-white builder-cursor-pointer builder-whitespace-nowrap builder-rounded builder-font-semibold'
            >
              <i className='fal fa-trash-alt builder-mr-2' />
              Delete selected ({selectedIds.size})
            </div>
          )}
          <div className='builder-flex builder-items-center builder-rounded builder-border builder-border-gray-300 builder-divide-x builder-divide-gray-300 builder-overflow-hidden'>
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'inactive', label: 'Inactive' }
            ].map((option) => (
              <div
                key={option.key}
                onClick={() => setStatusFilter(option.key)}
                data-testid={`advertisements-status-filter-${option.key}`}
                className={`builder-px-4 builder-py-2 builder-text-sm builder-cursor-pointer ${statusFilter === option.key ? 'builder-bg-primary builder-text-white builder-font-semibold' : 'builder-bg-white builder-text-gray-700'}`}
              >
                {option.label}
              </div>
            ))}
          </div>
          <div>
            <Search onChange={(newSearchTerm) => setSearch(newSearchTerm)} value={search} dataTestId='advertisements-search-button' placeholder='Search by description, group or filter' />
          </div>
          <div onClick={onCreateAdvertisement} className='builder-px-4 builder-py-2 builder-bg-primary builder-text-white builder-border builder-border-bg-blue-500 builder-cursor-pointer opacity-100 builder-whitespace-nowrap'>
            <i className='fal fa-plus builder-mr-2' />
            Add new
          </div>
        </div>
      </div>

      <div className='builder-flex builder-flex-col builder-w-full'>
        <div className='builder-overflow-x-auto'>
          <div className='builder-py-2 builder-align-middle builder-inline-block builder-min-w-full'>
            <div className='builder-shadow builder-overflow-hidden builder-border-b builder-border-gray-200'>
              <table className='builder-min-w-full builder-divide-y builder-divide-gray-200'>
                <thead className='builder-bg-gray-50'>
                  <tr>
                    <th scope='col' className='builder-pl-4 builder-pr-2 builder-py-3 builder-text-left' style={{ width: 36 }}>
                      <input
                        type='checkbox'
                        className='builder-cursor-pointer'
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = !allSelected && someSelected;
                        }}
                        onChange={toggleSelectAll}
                        data-testid='advertisements-select-all'
                      />
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '27%' }}>
                      Description
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '12%' }}>
                      Languages
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '13%' }}>
                      Group
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '10%' }}>
                      Status
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '17%' }}>
                      Filters
                    </th>
                    <th scope='col' className='builder-px-6 builder-py-3 builder-text-left builder-text-sm builder-font-bold builder-text-black builder-uppercase builder-tracking-wider' style={{ width: '15%' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='builder-bg-white builder-divide-y builder-divide-gray-200'>
                  {map(filteredAdvertisements, (advertisement) => {
                    const canEdit = canEditAdvertisement(advertisement);
                    const description = advertisement.description && advertisement.description !== '' ? advertisement.description : '<no description>';
                    const isActive = isAdvertisementActive(advertisement);

                    return (
                      <tr key={advertisement.id}>
                        <td className='builder-pl-4 builder-pr-2 builder-py-4' style={{ width: 36 }}>
                          <input
                            type='checkbox'
                            className='builder-cursor-pointer'
                            checked={selectedIds.has(advertisement.id)}
                            disabled={!canEdit}
                            onChange={() => toggleSelect(advertisement.id)}
                            data-testid={`advertisement-select-${advertisement.id}`}
                          />
                        </td>
                        <td className='builder-px-6' style={{ width: '27%' }}>
                          {description}
                        </td>
                        <td className='builder-px-6 builder-py-4' style={{ width: '12%' }}>
                          <div className='builder-flex builder-flex-col builder-gap-1.5'>
                            {map(languages, (language) => {
                              const hasContent = !!find(advertisement.content, (content) => content.culture === language);
                              return (
                                <span
                                  key={language}
                                  title={hasContent ? `Content available for ${language.toUpperCase()}` : `No content for ${language.toUpperCase()}`}
                                  className={`builder-inline-flex builder-items-center builder-text-sm builder-uppercase ${hasContent ? 'builder-text-black builder-font-semibold' : 'builder-text-gray-400'}`}
                                >
                                  <span className={`builder-w-2 builder-h-2 builder-rounded-full builder-mr-2 ${hasContent ? 'builder-bg-green-500' : 'builder-bg-gray-300'}`} />
                                  {language}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className='' style={{ width: '13%' }}>
                          {advertisement.groups && advertisement.groups.length > 0 ? (
                            map(advertisement.groups, (groupId) => {
                              const group = groupsById[groupId];
                              return (
                                <div key={groupId} className='builder-px-6 builder-py-4 builder-whitespace-nowrap builder-text-md builder-text-black'>
                                  {group?.name ? group.name : '-'}
                                </div>
                              );
                            })
                          ) : (
                            <div className='builder-px-6 builder-py-4 builder-whitespace-nowrap builder-text-md builder-text-black'>-</div>
                          )}
                        </td>
                        <td className='builder-px-6 builder-py-4 builder-whitespace-nowrap' style={{ width: '10%' }}>
                          <span
                            className={`builder-inline-flex builder-items-center builder-px-2 builder-py-1 builder-rounded-full builder-text-xs builder-font-semibold ${
                              isActive ? 'builder-bg-green-100 builder-text-green-700' : 'builder-bg-gray-100 builder-text-gray-600'
                            }`}
                            title={isActive ? 'At least one group is used (referenced in components or CMS)' : 'No used group — this advertisement will not be displayed'}
                          >
                            <span className={`builder-w-2 builder-h-2 builder-rounded-full builder-mr-1.5 ${isActive ? 'builder-bg-green-500' : 'builder-bg-gray-400'}`} />
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className='builder-px-6 builder-py-4 builder-text-md builder-text-black' style={{ width: '20%' }}>
                          {Array.isArray(advertisement.filters) && advertisement.filters.length > 0 ? (
                            <div className='builder-flex builder-flex-col builder-flex-wrap builder-gap-1'>
                              {map(advertisement.filters, (adFilter, index) => (
                                <div key={`${adFilter.type}-${adFilter.value}-${index}`} className='builder-bg-gray-100 builder-text-gray-700 builder-text-sm builder-px-2 builder-py-1 builder-rounded builder-w-fit'>
                                  {adFilter.value ? `${adFilter.type}: ${adFilter.value}` : adFilter.type}
                                </div>
                              ))}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className='builder-flex builder-px-6 builder-py-4 builder-whitespace-nowrap builder-text-md builder-text-black' style={{ width: '15%' }}>
                          {canEdit && (
                            <React.Fragment>
                              <div
                                className='builder-flex builder-items-center builder-cursor-pointer builder-border-r builder-border-gray-300 builder-mr-3 builder-pr-3'
                                onClick={() => onEditAdvertisement(advertisement.id, context?.culture || 'nl')}
                              >
                                Edit <i className='fal fa-pencil-alt builder-ml-1' style={{ fontSize: 12 }} />
                              </div>
                              <div className='builder-flex builder-items-center builder-cursor-pointer' onClick={() => setConfirmDelete(advertisement.id)}>
                                Delete <i className='fal fa-trash-alt builder-ml-1' style={{ fontSize: 12 }} />
                              </div>
                            </React.Fragment>
                          )}
                          {!canEdit && (
                            <React.Fragment>
                              <span className='builder-font-bold'>No actions allowed in draft environment</span>
                            </React.Fragment>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
